import ReactECharts from 'echarts-for-react';

/**
 * KernelVersionChart_3 — "dark command-deck" variant of KernelVersionChart (blog05).
 * Same real data: GPT-4 behavior drift, March 2023 → June 2023 (Chen, Zaharia &
 * Zou 2023, arXiv:2307.09009v3 — see KernelVersionChart.tsx header for the
 * per-task citations; mirrors public/blog/gpt4-drift-chen-2023.csv).
 * Opaque slate-900 card, light text, teal/cyan accents, subtle gridlines.
 */

const DECK = {
  bg: '#0F172A', // slate-900, opaque
  panel: '#1E293B', // tooltip surface
  grid: '#334155', // subtle gridlines
  primary: '#F8FAFC', // primary text
  secondary: '#94A3B8', // secondary text
  slateBar: '#64748B', // March bars
  teal: '#0AB3BF',
  cyan: '#4DD4E0',
};

const TASKS = [
  { label: 'Prime vs composite', metric: 'accuracy', n: 1000, march: 84.0, june: 51.1 },
  { label: 'Happy-number counting', metric: 'accuracy', n: 500, march: 83.6, june: 35.2 },
  { label: 'Code generation', metric: 'directly executable', n: 50, march: 52.0, june: 10.0 },
  { label: 'Sensitive questions', metric: 'response rate', n: 100, march: 21.0, june: 5.0 },
];

/** ONE shared percent formatter — tooltip, axis ticks, and bar labels all use it. */
const formatPercent = (v: number) => `${v}%`;

export const KernelVersionChart_3 = ({ height = 320 }: { height?: number }) => {
  const option = {
    backgroundColor: DECK.bg,
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow', shadowStyle: { color: 'rgba(148,163,184,0.08)' } },
      backgroundColor: DECK.panel,
      borderColor: DECK.grid,
      textStyle: { color: DECK.primary, fontSize: 13 },
      formatter: (params: { seriesName: string; value: number; dataIndex: number; marker?: string }[]) => {
        const t = TASKS[params[0].dataIndex];
        const rows = params.map((p) => `${p.marker} ${p.seriesName}: <b>${formatPercent(p.value)}</b>`).join('<br/>');
        return `${t.label} — GPT-4 ${t.metric} (n=${t.n})<br/>${rows}`;
      },
    },
    legend: {
      top: 8,
      textStyle: { color: DECK.primary, fontSize: 13 },
      inactiveColor: DECK.slateBar,
      data: ['March 2023', 'June 2023'],
    },
    grid: { left: 52, right: 24, top: 48, bottom: 60 },
    xAxis: {
      type: 'category',
      data: TASKS.map((t) => t.label),
      axisLine: { lineStyle: { color: DECK.grid } },
      axisLabel: { color: DECK.secondary, fontSize: 12, fontWeight: 600, interval: 0, width: 120, overflow: 'break' },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'value',
      max: 100,
      name: 'GPT-4 (%)',
      nameTextStyle: { color: DECK.secondary, fontSize: 11 },
      axisLabel: { color: DECK.secondary, fontSize: 12, formatter: (v: number) => formatPercent(v) },
      splitLine: { lineStyle: { color: DECK.grid, opacity: 0.4 } },
    },
    series: [
      {
        name: 'March 2023',
        type: 'bar',
        barGap: '12%',
        itemStyle: { color: DECK.slateBar, borderRadius: [3, 3, 0, 0] },
        data: TASKS.map((t) => t.march),
        label: {
          show: true,
          position: 'top',
          color: DECK.secondary,
          fontSize: 11,
          formatter: (p: { value: number }) => formatPercent(p.value),
        },
      },
      {
        name: 'June 2023',
        type: 'bar',
        itemStyle: { color: DECK.cyan, borderRadius: [3, 3, 0, 0] },
        data: TASKS.map((t) => t.june),
        label: {
          show: true,
          position: 'top',
          color: DECK.primary,
          fontSize: 11,
          fontWeight: 700,
          formatter: (p: { value: number }) => formatPercent(p.value),
        },
      },
    ],
  };

  return (
    <div className="my-8 rounded-xl overflow-hidden border border-slate-700 bg-slate-900 p-2">
      <ReactECharts option={option} style={{ height }} opts={{ renderer: 'canvas' }} />
      <p className="mt-2 text-xs text-slate-400 text-center">
        Figure 1. Same model name, different behavior, three months apart. Without a versioned contract, your
        agent&apos;s behavior drifts silently. (Chen, Zaharia &amp; Zou 2023, arXiv:2307.09009v3)
      </p>
      <p className="mt-1 text-label-xs text-slate-500 text-center">
        v3 methodology (500 primes + 500 composites); v1&apos;s 97.6%→2.4% figures were revised by the authors.
        &ldquo;Sensitive questions&rdquo; is a response rate, not an accuracy.
      </p>
    </div>
  );
};
